// external
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// interstate components
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import type { DatePickerEventValue } from '@interstate/components/DatePicker';
import { DatePicker } from '@interstate/components/DatePicker';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@interstate/components/NumericInput';
import { Typography } from '@interstate/components/Typography';
import { TrashIcon } from '@interstate/components/Icons/TrashIcon';
import { dateUtils, formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';

// internal components
import { SlideOutWithFixedFooter } from '../utils/SlideOutWithFixedFooter';
import { COUNTS_TOWARD_TOTAL_CASH_DOWN } from '../constants';
import { useDispatch } from 'react-redux';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import { getDueAtSigning } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { validateDefermentAmount, validateDefermentDate, hasValidationErrors } from './defermentValidation';
import type { DefermentValidationErrors } from './defermentValidation';
import type { DeferredDownPayment } from '@makemydeal/dr-platform-types';
import {
    defermentFieldsContainerSx,
    defermentFooterActionsContainerSx,
    defermentTrashIconContainerSx,
    defermentTrashIconStyles
} from './DefermentSlideOut.style';
import { mergeDateWithCurrentUTCTime } from '../utils/formatUtils';

export type DefermentSlideOutProps = {
    show: boolean;
    onCancel: () => void;
    onApply: (values: DeferredDownPayment) => void;
    onClear: (values: DeferredDownPayment) => void;
    initialValues?: Partial<DeferredDownPayment>;
};

const DEFERMENT = 'Cash Down Deferment';
const DEFERMENT_AMOUNT = 'Deferment Amount';
const DEFERMENT_DATE = 'Deferment Date';
// Must match DatePicker's dateFormat prop ("MM/dd/yyyy")
const DATE_FORMAT = 'MM/dd/yyyy';

/**
 * Converts a date string to a Date object for DatePicker value prop.
 * @param dateString - Date string in various formats (ISO, MM/dd/yyyy, etc.)
 * @returns Date object for DatePicker, or null if empty/invalid
 */
const convertToDatePickerValue = (dateString: string): Date | null => {
    if (!dateString) return null;
    const formattedDate = dateUtils.formatUtcDateTimeToDateOnly(dateString, 'mmddyyyy') || dateString;
    return new Date(formattedDate);
};

const DefermentSlideOut: FC<DefermentSlideOutProps> = ({ show, onCancel, onApply, onClear, initialValues }) => {
    const dispatch = useDispatch();
    const cashDown = useSelector(getDueAtSigning);
    const [deferredPaymentAmount, setDeferredPaymentAmount] = useState<string>(
        initialValues?.amount !== undefined ? String(formatNumberAndCents(initialValues.amount)) : ''
    );
    const [deferredPaymentDate, setDeferredPaymentDate] = useState(initialValues?.paymentDate ?? '');
    const [isAmountTouched, setIsAmountTouched] = useState(false);
    const [isDateTouched, setIsDateTouched] = useState(false);

    const errors: DefermentValidationErrors = useMemo(() => {
        const newErrors: DefermentValidationErrors = {};
        const normalizedAmount = deferredPaymentAmount.replace(/[$,\s]/g, '');
        const parsedAmount = Number.parseFloat(normalizedAmount);

        const amountResult = validateDefermentAmount(parsedAmount, cashDown);
        if (!amountResult.isValid) {
            newErrors.amount = amountResult.error;
        }

        const dateResult = validateDefermentDate(deferredPaymentDate);
        if (!dateResult.isValid) {
            newErrors.date = dateResult.error;
        }

        return newErrors;
    }, [deferredPaymentAmount, deferredPaymentDate, cashDown]);

    const hasErrors = hasValidationErrors(errors);
    const canApply = !hasErrors;

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const handleApply = useCallback(() => {
        const normalizedAmount = deferredPaymentAmount.replace(/[$,\s]/g, '');
        const parsedAmount = Number.parseFloat(normalizedAmount);

        // Convert date to ISO format for Deal API compatibility
        const isoPaymentDate = mergeDateWithCurrentUTCTime(deferredPaymentDate, DATE_FORMAT) || deferredPaymentDate;

        dispatch(offerActionCreators.updateDeferredDownPayment(parsedAmount, isoPaymentDate));
        onApply({
            amount: parsedAmount,
            paymentDate: isoPaymentDate
        });
    }, [dispatch, deferredPaymentAmount, deferredPaymentDate]);

    const handleDateChange = (e: InterstateOnChangeEvent<DatePickerEventValue>) => {
        const dateValue = e.target.value && e.target.value.dateValue ? e.target.value.dateValue : '';
        setDeferredPaymentDate(dateValue || '');
        setIsDateTouched(true);
    };

    const handleDateBlur = useCallback(() => {
        setIsDateTouched(true);
    }, []);

    const handleAmountChange = useCallback((value: string) => {
        setDeferredPaymentAmount(formatNumberAndCents(value));
        setIsAmountTouched(true);
    }, []);

    const handleClearDeferment = () => {
        setDeferredPaymentAmount('');
        setDeferredPaymentDate('');
        setIsAmountTouched(false);
        setIsDateTouched(false);
        dispatch(offerActionCreators.updateDeferredDownPayment(0, ''));
        onClear({
            amount: 0,
            paymentDate: ''
        });
    };

    return (
        <SlideOutWithFixedFooter
            show={show}
            onHide={handleCancel}
            header={DEFERMENT}
            position="right"
            data-testid="deferment-slideout"
            panelWidth="50%"
        >
            <Box sx={defermentFieldsContainerSx}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <NumericInput
                        label={DEFERMENT_AMOUNT}
                        name={kebabCase(DEFERMENT_AMOUNT)}
                        data-testid="deferment-amount"
                        value={deferredPaymentAmount}
                        placeholder="0.00"
                        onChange={(e: InterstateOnChangeEvent<string>) => handleAmountChange(e.target.value)}
                        allowDecimal={true}
                        decimalMaxLength={2}
                        decimalMinLength={0}
                        inputPrefix={'$'}
                        autoInsertCommas={true}
                        hasError={isAmountTouched && !!errors.amount}
                        errorMessage={isAmountTouched ? errors.amount : undefined}
                        sx={{
                            '& input': {
                                textAlign: 'right'
                            }
                        }}
                    />
                    <Typography
                        variant="label-md"
                        color="sem.color.on-surface.muted"
                        sx={{ marginTop: 1 }}
                        data-testid="deferment-amount-helper"
                    >
                        {COUNTS_TOWARD_TOTAL_CASH_DOWN}
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <DatePicker
                        id={kebabCase(DEFERMENT_DATE)}
                        data-testid="deferment-date"
                        label={DEFERMENT_DATE}
                        dateFormat={DATE_FORMAT}
                        value={convertToDatePickerValue(deferredPaymentDate)}
                        onBlur={handleDateBlur}
                        onChange={handleDateChange}
                        locale="en-US"
                        hasError={isDateTouched && !!errors.date}
                        errorMessage={isDateTouched ? errors.date : undefined}
                    />
                </Box>
                <Box sx={defermentTrashIconContainerSx}>
                    <TrashIcon
                        data-testid="deferment-clear"
                        color="#005ba8"
                        style={defermentTrashIconStyles}
                        onClick={handleClearDeferment}
                    />
                </Box>
            </Box>

            <Box sx={defermentFooterActionsContainerSx}>
                <Button buttonStyle="tertiary" onClick={handleCancel} data-testid="deferment-cancel">
                    Cancel
                </Button>
                <Button buttonStyle="primary" onClick={handleApply} data-testid="deferment-apply" disabled={!canApply}>
                    Apply
                </Button>
            </Box>
        </SlideOutWithFixedFooter>
    );
};

export default DefermentSlideOut;
